import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { LogOut } from "lucide-react";
import { API, StatusPill } from "./shared";

const NAV = [
    { label: "Overview", href: "/employer" },
    { label: "Company Profile", href: "/employer/profile" },
    { label: "Verification", href: "/employer/verification" },
    { label: "Create Job", href: "/employer/jobs/new" },
    { label: "Manage Jobs", href: "/employer/jobs" },
    { label: "Applicants", href: "/employer/applicants" },
    { label: "Shortlisted", href: "/employer/shortlisted" },
    { label: "Interviews", href: "/employer/interviews" },
    { label: "Recruitment Requests", href: "/employer/requests" },
    { label: "Messages", href: "/employer/messages" },
    { label: "Documents", href: "/employer/documents" },
    { label: "Notifications", href: "/employer/notifications" },
    { label: "Settings", href: "/employer/settings" },
];

export const useEmployerAuth = () => {
    const navigate = useNavigate();
    const [user, setUser] = useState(null);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const res = await fetch(`${API}/api/employer/me`, { credentials: "include" });
                if (!res.ok) throw new Error();
                const data = await res.json();
                if (active) setUser(data);
            } catch {
                navigate("/employer/login");
            }
        })();
        return () => {
            active = false;
        };
    }, [navigate]);

    const logout = async () => {
        await fetch(`${API}/api/employer/logout`, { method: "POST", credentials: "include" });
        navigate("/employer/login");
    };

    return { user, logout };
};

export const PortalLoading = () => (
    <div className="flex min-h-screen items-center justify-center bg-night">
        <div className="h-7 w-7 animate-spin rounded-full border-2 border-champagne border-t-transparent" />
    </div>
);

export const EmployerMark = ({ light = false }) => (
    <span data-testid="employer-portal-logo" className={`relative flex h-11 w-11 shrink-0 items-center justify-center ${light ? "bg-ivory text-forest" : "bg-forest text-ivory"}`}>
        <svg viewBox="0 0 44 44" aria-hidden="true" className="absolute inset-0 h-full w-full">
            <path d="M22 5 39 22 22 39 5 22Z" fill="none" stroke="currentColor" strokeOpacity="0.34" />
            <path d="M13 27c5.5-8.5 12.5-8.5 18 0" fill="none" stroke="#D4AF37" strokeWidth="1.6" strokeLinecap="square" />
            <path d="M15 17h14" stroke="currentColor" strokeWidth="1.2" strokeOpacity="0.7" />
        </svg>
        <span className="relative font-serif text-[10px] font-semibold tracking-[0.12em]">AGL</span>
    </span>
);

const isActive = (pathname, href) => {
    if (href === "/employer") return pathname === href;
    if (href === "/employer/jobs") return pathname === href || pathname.startsWith("/employer/jobs/");
    return pathname.startsWith(href);
};

export const EmployerLayout = ({ title, subtitle, children, actions }) => {
    const { user, logout } = useEmployerAuth();
    const { pathname } = useLocation();

    useEffect(() => {
        const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
        let frame;
        const raf = (time) => {
            lenis.raf(time);
            frame = requestAnimationFrame(raf);
        };
        frame = requestAnimationFrame(raf);
        return () => {
            cancelAnimationFrame(frame);
            lenis.destroy();
        };
    }, []);

    if (!user) return <PortalLoading />;

    return (
        <div className="min-h-screen bg-ivory text-ink" data-testid="employer-portal">
            <aside className="border-b border-ivory/10 bg-night text-ivory lg:fixed lg:inset-y-0 lg:left-0 lg:z-40 lg:flex lg:w-72 lg:flex-col lg:border-b-0 lg:border-r">
                <div className="flex items-center justify-between gap-5 px-6 py-6 lg:block lg:px-8 lg:py-8">
                    <a href="/" data-testid="employer-home-link" className="flex items-center gap-4">
                        <EmployerMark />
                        <span className="leading-tight">
                            <span className="block font-serif text-lg font-semibold uppercase tracking-[0.14em]">Afrinexa</span>
                            <span className="block text-[9px] font-medium uppercase tracking-[0.32em] text-ivory/50">Employer Portal</span>
                        </span>
                    </a>
                    <button
                        type="button"
                        data-testid="employer-logout-button"
                        onClick={logout}
                        className="inline-flex items-center gap-2 border border-ivory/20 px-3.5 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-ivory/70 transition-colors hover:border-champagne hover:text-champagne lg:hidden"
                    >
                        <LogOut size={13} strokeWidth={1.5} /> Sign Out
                    </button>
                </div>

                <nav data-testid="employer-nav" className="flex gap-1 overflow-x-auto px-4 pb-4 lg:mt-6 lg:flex-1 lg:flex-col lg:overflow-visible lg:px-4 lg:pb-0">
                    {NAV.map((item, index) => (
                        <a
                            key={item.href}
                            href={item.href}
                            data-testid={`employer-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={`group flex shrink-0 items-center gap-3 px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.15em] transition-colors duration-300 ${
                                isActive(pathname, item.href)
                                    ? "bg-ivory text-forest"
                                    : "text-ivory/55 hover:bg-ivory/[0.06] hover:text-ivory"
                            }`}
                        >
                            <span className={`font-serif text-xs ${isActive(pathname, item.href) ? "text-champagne" : "text-ivory/30 group-hover:text-champagne"}`}>
                                {String(index + 1).padStart(2, "0")}
                            </span>
                            {item.label}
                        </a>
                    ))}
                </nav>

                <div className="hidden border-t border-ivory/10 p-6 lg:block">
                    <StatusPill status={user.verification_status || "pending"} type="verification" dark />
                    <p className="mt-3 truncate text-xs text-ivory/45">{user.email}</p>
                    <button
                        type="button"
                        data-testid="employer-logout-button-desktop"
                        onClick={logout}
                        className="mt-4 inline-flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-ivory/55 transition-colors hover:text-champagne"
                    >
                        <LogOut size={13} strokeWidth={1.5} /> Sign Out
                    </button>
                </div>
            </aside>

            <main className="lg:pl-72">
                <motion.div
                    key={pathname}
                    initial={{ opacity: 0, y: 18 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                    className="mx-auto max-w-7xl px-6 py-8 sm:py-10 lg:px-10"
                >
                    <div className="flex flex-wrap items-end justify-between gap-5 border-b border-ink/10 pb-8">
                        <div>
                            <p className="text-[10px] font-semibold uppercase tracking-[0.3em] text-champagne">Afrinexa Global • Employers</p>
                            <h1 className="mt-3 font-serif text-3xl font-medium tracking-tight text-ink sm:text-4xl">{title}</h1>
                            {subtitle && <p className="mt-2 max-w-2xl text-sm leading-relaxed text-ink/55">{subtitle}</p>}
                        </div>
                        {actions && <div className="flex flex-wrap gap-3">{actions}</div>}
                    </div>
                    <div className="py-8">{children}</div>
                </motion.div>
            </main>
        </div>
    );
};
